import { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * useSharedWithMe — subscribes to the user's `sharedWithMe` collection,
 * which holds pointer docs for boards/lists that other users have
 * shared with them.
 *
 * Pointer docs are written by useResolvedBoard / useResolvedList the
 * first time the user lands on a shared URL, AND server-side by
 * acceptShareInvite when a user accepts an invite. The Sidebar /
 * SharedWithMePage / WelcomeSetup gate all read this collection.
 *
 * Doc shape (see SHARING_SPEC.md §3):
 *   {
 *     kind: 'board' | 'list',
 *     refOwnerUid: string,
 *     refId: string,
 *     refBoardId?: string,        // for kind='list'
 *     role: 'editor' | 'viewer',
 *     cachedTitle: string,
 *     addedAt: Timestamp,
 *   }
 *
 * Returns { items, loading } where:
 *   - items: docs (with `id` added) sorted most-recently-added first.
 *     [] for users who haven't been shared anything OR while loading.
 *   - loading: true until the first snapshot resolves. Critical for the
 *     WelcomeSetup gate in App.js — without this distinction, "fresh
 *     user with 0 boards" looks identical to "fresh user with shares
 *     not yet loaded", and welcome fires inappropriately for the latter.
 */
export function useSharedWithMe(userId) {
  const [items, setItems] = useState([]);
  // resolvedFor tracks which userId we last successfully resolved
  // data for. Deriving `loading` from the comparison (rather than a
  // separate state set inside an effect) eliminates the render
  // window where userId has become defined but the effect hasn't
  // yet run setLoading(true). That window was letting the welcome
  // popup fire on fresh share-recipients before their share pointer
  // doc had a chance to load, undoing the gate this hook is meant
  // to support.
  const [resolvedFor, setResolvedFor] = useState(undefined);
  const loading = userId !== resolvedFor;

  useEffect(() => {
    if (!userId) {
      setItems([]);
      setResolvedFor(null);
      return undefined;
    }
    const q = query(
      collection(db, 'users', userId, 'sharedWithMe'),
      orderBy('addedAt', 'desc'),
    );
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setItems(docs);
      setResolvedFor(userId);
    }, (err) => {
      // Snapshot errors are logged but non-fatal — the section just
      // doesn't render. Most likely cause would be rules denials,
      // which shouldn't happen since the user reads their own subtree.
      console.warn('[useSharedWithMe] snapshot error', err);
      setItems([]);
      setResolvedFor(userId);
    });
    return unsub;
  }, [userId]);

  return { items, loading };
}
